import { Container, Row, Col, Button, Badge, Form, InputGroup, Card, Offcanvas } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { Send, MessageSquare, X, UserRound, BotMessageSquare } from "lucide-react";
import Swal from "sweetalert2";

import { useUserTopic } from "../../hooks/ai-hooks/useCommSkillsTopic";
import { useConversationTimer } from "../../hooks/ai-hooks/useConversationTimer";
import { useSpeechController } from "../../hooks/ai-hooks/useSpeechController";
import { useSessionManager } from "../../hooks/ai-hooks/useSessionManager";
import { useUserVideo } from "../../hooks/useUserVideo";
import { AIPanel } from "../../components/AI/AIPanel";
import { MicController } from "../../components/AI/MicController";
import { UserPanel } from "../../components/AI/UserPanel";

import { useMediaPermissions } from "../../hooks/useMediaPermissions";
import { useStreamingAI } from "../../hooks/ai-hooks/useStreamingAI";
// import { useAISession } from "../../hooks/ai-hooks/useCommunicationSession";
import { BASE_URL } from "../../config/create-header";
import { apiUrl } from "../../config/apiUrls";
import useCommConversationStore from "../../hooks/ai-hooks/useCommConversationStore";
import PageHeader from "../../components/PageHeader";

const StartCommunicationPrep = () => {
    const [textInput, setTextInput] = useState("");
    const [textTimer, setTextTimer] = useState(null);
    const TEXT_TIME_LIMIT = 45;
    const [showChat, setShowChat] = useState(false);
    const chatEndRef = useRef(null);

    // Conversation Store
    const { messages, addMessage, setInputSource, inputSource, clearMessages } = useCommConversationStore();

    // Hooks
    const { id } = useParams();
    const ai = useStreamingAI();
    // const { sendUserMessage, loading, speechStatus, AIresponse, text: aiText } = useAISession();

    // Create an object to mimic or replace `ai` usage where appropriate, or just use variables directly
    // const ai = {
    //     loading,
    //     text: aiText,
    //     typing: loading, // rough mapping
    //     // speaking: speechStatus === "speaking" // if needed
    // };

    const video = useUserVideo();
    const navigate = useNavigate();
    const { modules } = useUserTopic();
    const media = useMediaPermissions();

    // Compute speechStatus from speaking boolean - now from hook directly
    const speechStatus = ai.speaking ? "speaking" : "idle";

    const sessionManager = useSessionManager(id, "/practice-with-ai/communication-skills", "CommunicationId");

    useEffect(() => {
        if (sessionManager.hasRefreshed) {
            console.log("User refreshed the page, session data may be lost");
        }
    }, [sessionManager.hasRefreshed]);


    const speech = useSpeechController((text) => {
        addMessage("USER", text);
        ai.sendMessage(`${BASE_URL}${apiUrl.Communication}`, {
            userMessage: text,
            communicationId: id,
        })
        // sendUserMessage(text, id);
    });

    // Scroll chat to bottom when new messages arrive
    useEffect(() => {
        if (chatEndRef.current && showChat) {
            chatEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages, showChat]);

    // Track AI responses and add to conversation
    useEffect(() => {
        if (ai.text && !ai.loading && !ai.typing) {
            addMessage("AI", ai.text);
        }
    }, [ai.text, ai.loading, ai.typing]);

    // Track input source based on mic status
    useEffect(() => {
        if (speech.listening) {
            setInputSource("voice");
        } else if (textInput.trim()) {
            setInputSource("text");
        } else {
            setInputSource(null);
        }
    }, [speech?.listening, textInput]);

    // Check if input should be disabled (mic is active, AI is busy, or timer expired)
    const isBusy = speech.listening || speechStatus === "speaking" || ai.loading;
    const isTypingDisabled = isBusy || textTimer === 0;
    const isSendDisabled = isBusy || !textInput.trim();

    const handleSendTextMessage = () => {
        if (!isSendDisabled) {
            addMessage("USER", textInput);
            ai.sendMessage(`${BASE_URL}${apiUrl.Communication}`, {
                userMessage: textInput,
                communicationId: id,
            })
            // sendUserMessage(textInput, id);
            setTextInput("");
            setTextTimer(null);
        }
    };

    // Handle Enter key press
    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendTextMessage();
        }
    };

    const timer = useConversationTimer(
        parseInt(modules?.communication?.duration || 10) * 60,
        () => {
            Swal.fire({
                title: "Session Ended",
                text: "The conversation time has expired.",
                icon: "info",
                confirmButtonColor: "#0d6efd"
            }).then(() => {
                navigate("/practice-with-ai/communication-skills");
            });
        }
    );

    // Browser Support Check
    useEffect(() => {
        // Speech Recognition Support
        if (speech.browserSupportsSpeechRecognition === false) {
            Swal.fire({
                title: "Speech Recognition Not Supported",
                text: "Your browser does not support speech recognition. Please use Chrome or Edge for the best experience.",
                icon: "error",
                confirmButtonColor: "#0d6efd"
            });
        }
    }, [speech.browserSupportsSpeechRecognition]);

    // Text Input Timer Logic
    useEffect(() => {
        let interval;
        if (textTimer !== null && textTimer > 0) {
            interval = setInterval(() => {
                setTextTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [textTimer]);

    // Start timer when user starts typing
    useEffect(() => {
        if (textInput.trim().length > 0 && textTimer === null && !isBusy) {
            setTextTimer(TEXT_TIME_LIMIT);
        } else if (textInput.trim().length === 0 && textTimer !== null) {
            setTextTimer(null);
        }
    }, [textInput, isBusy]);

    useEffect(() => {
        // Camera Support
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            Swal.fire({
                title: "Camera Not Supported",
                text: "Your browser does not support camera access or you are in a non-secure context (HTTP).",
                icon: "error",
                confirmButtonColor: "#0d6efd"
            });
        }
    }, []);

    useEffect(() => {
        if (speech.listening) {
            ai.interrupt();
        }
    }, [speech.listening]);


    // Auto-restart Mic after AI stops speaking
    useEffect(() => {
        if (timer.started && speechStatus === "idle" && !ai.loading && !speech.listening) {
            // Give a small delay to prevent immediate echo if any
            const t = setTimeout(() => {
                speech.start();
            }, 500);
            return () => clearTimeout(t);
        }
    }, [speechStatus, timer.started, ai.loading]);

    // Cleanup video on unmount
    useEffect(() => {
        return () => {
            video.stopVideo();
        };
    }, []);

    // Clear messages when session ends
    useEffect(() => {
        return () => {
            clearMessages();
        };
    }, []);

    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
    };

    const formatMessageTime = (timestamp) => {
        const date = new Date(timestamp);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const unlockAudio = () => {
        const u = new SpeechSynthesisUtterance('');
        window.speechSynthesis.speak(u);
        window.speechSynthesis.cancel();
    };

    const handleStartSession = async () => {
        unlockAudio();
        if (!media.micGranted) {
            const result = await Swal.fire({
                title: "Microphone Required",
                text: "We need microphone access to practice communication skills. Would you like to grant permission now?",
                icon: "info",
                showCancelButton: true,
                confirmButtonText: "Grant Permission",
                cancelButtonText: "Later",
                confirmButtonColor: "#0d6efd",
                cancelButtonColor: "#6c757d",
            });

            if (result.isConfirmed) {
                const granted = await media.refresh();
                if (!granted) {
                    Swal.fire({
                        title: "Permission Denied",
                        text: "Please enable microphone access in your browser settings and try again.",
                        icon: "warning",
                        confirmButtonColor: "#0d6efd"
                    });
                    return;
                }
            } else {
                return;
            }
        }
        timer.start();
        ai.sendMessage(`${BASE_URL}${apiUrl.CommunicationFirstStart}`, {
            communicationId: id,
        });
    };


    const handleEndSession = () => {
        Swal.fire({
            title: "End Session?",
            text: "Are you sure you want to end this practice session?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#dc3545",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Yes, end it"
        }).then((result) => {
            if (result.isConfirmed) {
                video.stopVideo();
                clearMessages();
                navigate("/practice-with-ai/communication-skills");
            }
        });
    };


    // Chat Message Component
    const ChatMessage = ({ message }) => (
        <div className={`d-flex ${message.sender === 'USER' ? 'justify-content-end' : 'justify-content-start'} mb-3`}>
            <div
                className={`d-flex align-items-end gap-2 ${message.sender === 'USER' ? 'flex-row-reverse' : ''}`}
                style={{ maxWidth: '80%' }}
            >
                {/* Avatar */}
                <div
                    className="flex-shrink-0 rounded-circle d-flex align-items-center justify-content-center border border-2 border-dark"
                    style={{ width: '32px', height: '32px' }}
                >
                    {message.sender === 'USER' ? <UserRound size={18} /> : <BotMessageSquare size={18} />}
                </div>

                {/* Message Bubble */}
                <div
                    // className={`p-3 ${message.sender === 'USER'
                    className={`p-3 ${message.sender === 'USER'
                        ? 'bg-primary text-white'
                        : 'bg-white border'
                        }`}
                    style={{
                        borderRadius: message.sender === 'USER'
                            ? '18px 18px 4px 18px'
                            : '18px 18px 18px 4px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)'
                    }}
                >
                    <p className="mb-1" style={{ fontSize: '0.95rem', lineHeight: '1.5' }}>
                        {message.content}
                    </p>
                    <small
                        // className={`d-block text-end ${
                        // message.sender === 'USER' ? 'text-white-50' : 'text-muted'
                        className={`d-block text-end ${message.sender === 'USER' ? 'text-white-50' : 'text-muted'
                            }`}
                        style={{ fontSize: '0.7rem' }}
                    >
                        {formatMessageTime(message.timestamp)}
                    </small>
                </div>
            </div>
        </div>
    );

    return (
        <>
            <PageHeader
                title="Practice With AI"
                breadcrumbs={[
                    { label: "Dashboard", path: "/dashboard" },
                    { label: "Practice With AI", path: "/practice-with-ai" },
                    { label: "Communication Topic", active: true },
                ]}
            />
            <div style={{ display: 'flex', flexDirection: 'column' }} className="">
                {/* Header Section */}
                <div className="p-3 p-md-4 flex-shrink-0 bg-light mb-4">
                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-2">
                        <p className="mb-0 text-capitalize small-mobile-text">
                            Communication Topic: <b>{modules?.communication?.topic?.replace(/[_-]/g, " ") || "Loading..."}</b>
                        </p>
                        {timer.started && (
                            <div className="d-flex align-items-center gap-2 gap-md-3 ms-auto ms-sm-0">
                                <h5 className="mb-0 text-primary d-none d-md-block">
                                    Time Left: {formatTime(timer.timeLeft)}
                                </h5>
                                <Badge bg="primary" className="d-md-none">{formatTime(timer.timeLeft)}</Badge>
                                <Button variant="outline-danger" size="sm" onClick={handleEndSession} className="py-1">
                                    End Session
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                {/* Main Content Area - Scrollable */}
                <div className="flex-grow-1 overflow-auto px-4 pb-4">
                    <Row className="justify-content-center">
                        <Col lg={6} md={6}>
                            <AIPanel
                                loading={ai.loading}
                                speaking={speechStatus === "speaking"}
                                text={ai.text}
                                typing={ai.typing}
                            />
                        </Col>
                        <Col lg={6} md={6}>
                            <UserPanel
                                videoRef={video.videoRef}
                                videoOn={video.videoOn}
                                onVideoToggle={async () => {
                                    if (video.videoOn) {
                                        video.stopVideo();
                                    } else {
                                        try {
                                            await video.startVideo();
                                        } catch (error) {
                                            const result = await Swal.fire({
                                                title: "Camera Access Required",
                                                text: "Please allow camera access to show your video. Would you like to try again?",
                                                icon: "info",
                                                showCancelButton: true,
                                                confirmButtonText: "Grant Permission",
                                                cancelButtonText: "Cancel",
                                                confirmButtonColor: "#0d6efd",
                                                cancelButtonColor: "#6c757d",
                                            });

                                            if (result.isConfirmed) {
                                                try {
                                                    await video.startVideo();
                                                } catch {
                                                    Swal.fire({
                                                        title: "Permission Denied",
                                                        text: "Please enable camera access in your browser settings.",
                                                        icon: "warning",
                                                        confirmButtonColor: "#0d6efd"
                                                    });
                                                }
                                            }
                                        }
                                    }
                                }}
                                speaking={speech.listening}
                            />
                        </Col>
                    </Row>
                </div>

                {/* Conversation History Offcanvas */}
                <Offcanvas
                    show={showChat}
                    onHide={() => setShowChat(false)}
                    placement="start"
                    className="bg-light"
                    style={{ width: 'min(600px, 90vw)' }}
                >
                    <Offcanvas.Header closeButton className="bg-white border-bottom shadow-sm">
                        <Offcanvas.Title className="d-flex align-items-center gap-2">
                            <MessageSquare size={22} className="text-primary" />
                            <span className="fw-bold">Chat History</span>
                            <Badge bg="primary" pill className="ms-2">{messages.length}</Badge>
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body
                        className="p-0 d-flex flex-column"
                        style={{
                            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                        }}
                    >
                        <div className="flex-grow-1 overflow-auto p-3">
                            {messages.length === 0 ? (
                                <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted py-5 text-center">
                                    <MessageSquare size={48} className="mb-3 opacity-25" />
                                    <p className="mb-0">No messages yet</p>
                                    <small>The history of your conversation will appear here.</small>
                                </div>
                            ) : (
                                <>
                                    {messages.map((message) => (
                                        <ChatMessage key={message.id} message={message} />
                                    ))}
                                    <div ref={chatEndRef} />
                                </>
                            )}
                        </div>
                    </Offcanvas.Body>
                </Offcanvas>

                {/* Bottom Section - Controls */}
                <div className="p-3 bg-white border-top shadow-lg flex-shrink-0">
                    <div className="container mx-auto">
                        {!timer.started ? (
                            <div className="d-flex justify-content-center py-2">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="px-5 shadow-sm"
                                    onClick={handleStartSession}
                                    disabled={media.checking}
                                >
                                    {media.checking ? "Checking Permissions..." : "Start Session Now"}
                                </Button>
                            </div>
                        ) : (

                            <>
                                {/* Control Row */}
                                <Col xs={12}>
                                    <div className="d-flex align-items-center justify-content-between gap-2 gap-md-3">
                                        {/* History & Mic in a compact group on mobile */}
                                        <div className="d-flex align-items-center gap-2">
                                            <Button
                                                variant={showChat ? "primary" : "outline-primary"}
                                                size="md"
                                                className="px-2 px-md-3 shadow-sm d-flex align-items-center justify-content-center"
                                                onClick={() => setShowChat(true)}
                                                style={{ height: '45px', minWidth: '45px' }}
                                                title="History"
                                            >
                                                <MessageSquare size={18} />
                                                <span className="d-none d-lg-inline ms-2">History</span>
                                                {messages.length > 0 && (
                                                    <Badge
                                                        bg={showChat ? "light" : "primary"}
                                                        text={showChat ? "dark" : "white"}
                                                        pill
                                                        className="ms-1"
                                                        style={{ fontSize: '0.65rem' }}
                                                    >
                                                        {messages.length}
                                                    </Badge>
                                                )}
                                            </Button>
                                        </div>

                                        {/* Text Input - Expanded */}
                                        <div className="d-flex align-items-center justify-content-start w-100 gap-2">
                                            <MicController
                                                listening={speech.listening}
                                                timer={speech.timer}
                                                onToggle={() => speech.listening ? speech.stop() : speech.start()}
                                                disabled={speechStatus === "speaking" || ai.loading}
                                            />
                                            <div>OR</div>
                                            <div className="flex-grow-1">
                                                <InputGroup className="shadow-sm ">
                                                    <Form.Control
                                                        type="text"
                                                        placeholder={
                                                            speech.listening ? "Mic..." :
                                                                textTimer === 0 ? "Send now!" :
                                                                    "Type message..."
                                                        }
                                                        value={textInput}
                                                        onChange={(e) => setTextInput(e.target.value)}
                                                        onKeyPress={handleKeyPress}
                                                        disabled={isTypingDisabled}
                                                        className="py-2"
                                                        style={{
                                                            fontSize: '0.9rem',
                                                            height: '45px',
                                                            backgroundColor: (speech.listening || textTimer === 0) ? '#f8f9fa' : 'white',
                                                            cursor: (speech.listening || textTimer === 0) ? 'not-allowed' : 'text',
                                                            borderRight: 'none'
                                                        }}
                                                    />
                                                    {textTimer !== null && (
                                                        <InputGroup.Text
                                                            className={`${textTimer < 10 ? 'text-danger fw-bold animate-pulse' : 'text-muted'} bg-white border-start-0 border-end-0 px-2`}
                                                            style={{ fontSize: '0.75rem' }}
                                                        >
                                                            {textTimer}s
                                                        </InputGroup.Text>
                                                    )}
                                                    <Button
                                                        variant={(textTimer === 0 && textInput.trim()) ? "success" : textTimer === 0 ? "secondary" : "primary"}
                                                        onClick={handleSendTextMessage}
                                                        disabled={isSendDisabled}
                                                        className="px-3"
                                                        style={{ borderRadius: '0 4px 4px 0', height: '45px' }}
                                                    >
                                                        <Send size={18} />
                                                    </Button>
                                                </InputGroup>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </>
                        )}
                    </div>
                </div>


                {/* Custom Styles */}
                <style>{`
                @keyframes pulse {
                    0% { opacity: 1; }
                    50% { opacity: 0.7; }
                    100% { opacity: 1; }
                }
                .animate-pulse {
                    animation: pulse 1.5s infinite ease-in-out;
                }
                
                @media (max-width: 576px) {
                    .small-mobile-text {
                        font-size: 0.85rem;
                    }
                }
                
                .overflow-auto::-webkit-scrollbar {
                    width: 6px;
                }
                
                .overflow-auto::-webkit-scrollbar-track {
                    background: #f1f1f1;
                    border-radius: 3px;
                }
                
                .overflow-auto::-webkit-scrollbar-thumb {
                    background: #c1c1c1;
                    border-radius: 3px;
                }
                
                .overflow-auto::-webkit-scrollbar-thumb:hover {
                    background: #a8a8a8;
                }
            `}</style>
            </div>
        </>
    );
};

export default StartCommunicationPrep;
